import { AppStyle } from '../../../../style/styleUtilities'
import { OverrideFunc } from '../../../../model/Override'
import { Interpolation } from '@emotion/react'
import { Theme } from '@mui/material/styles'
import { ColorTokens, Palette } from 'src/feature/theme/model/Palette'

const BUTTON_RADIUS = '10px'
const BUTTON_PADDING = '3px 7px'
const BUTTON_TRANSITION = 'filter 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease, background-color 0.2s ease'
const BUTTON_HOVER_LIFT = 'translateY(-1px)'
const CONTAINED_HOVER_SHADOW = '0 4px 10px rgba(0, 0, 0, 0.18)'
const LIGHT_HOVER_SHADOW = '0 4px 10px rgba(0, 0, 0, 0.08)'
const DANGER_HOVER_SHADOW = `0 4px 10px ${Palette.DANGER}4D`
const DARK_HOVER_SHADOW = `0 4px 10px ${Palette.DEEP_SEA_BLUE}4D`
const DISABLED_CURSOR_STYLE = {
  pointerEvents: 'auto',
  cursor: 'not-allowed',
} as const

const buttonShapeStyle = {
  borderRadius: BUTTON_RADIUS,
  padding: BUTTON_PADDING,
}

const filledButtonStyle = {
  color: `${Palette.WHITE} !important`,
}

const createRippleStyle = (color: string) => ({
  '.MuiTouchRipple-child, .MuiTouchRipple-rippleVisible, .MuiTouchRipple-childLeaving, .MuiTouchRipple-ripple': {
    backgroundColor: `${color} !important`,
  },
  '.MuiTouchRipple-ripplePulsate': {
    backgroundColor: 'unset !important',
  },
})

const containedButtonStyle = {
  ...filledButtonStyle,
  border: `0 solid ${Palette.MIST_WHISPER} !important`,
  background: `linear-gradient(180deg, ${Palette.DEEP_SEA_BLUE} 0%, ${Palette.CORAL_REEF_GREEN} 100%) !important`,
}

const lightButtonStyle = {
  color: `${Palette.DEEP_SEA_BLUE} !important`,
  border: `1px solid ${Palette.DEEP_SEA_BLUE} !important`,
  background: `${Palette.WHITE} !important`,
}

const dangerButtonStyle = {
  ...filledButtonStyle,
  border: `1px solid ${Palette.DANGER} !important`,
  background: `${Palette.DANGER} !important`,
}

const darkButtonStyle = {
  ...filledButtonStyle,
  border: `1px solid ${Palette.DEEP_SEA_BLUE} !important`,
  background: `${Palette.DEEP_SEA_BLUE} !important`,
}

export const MuiButton: OverrideFunc<'MuiButton'> = theme => ({
  defaultProps: {
    disableElevation: true,
    variant: 'outlined',
  },
  styleOverrides: {
    root: {
      textAlign: 'center',
      alignItems: 'center',
      ...buttonShapeStyle,
      transition: BUTTON_TRANSITION,
      '&.Mui-disabled': {
        ...DISABLED_CURSOR_STYLE,
      },
    },
    light: createRippleStyle(theme.palette.grey['100']),
    danger: createRippleStyle(Palette.WHITE),
    outlined: {
      '.MuiTouchRipple-child, .MuiTouchRipple-rippleVisible, .MuiTouchRipple-childLeaving, .MuiTouchRipple-ripple': {
        backgroundColor: `${theme.palette.primary.lighter} !important`,
      },
      '.MuiTouchRipple-childPulsate': {
        backgroundColor: `${theme.palette.primary.light} !important`,
      },
      '.MuiTouchRipple-ripplePulsate': {
        backgroundColor: `unset !important`,
      },
    },
    startIcon: {
      fontSize: '1em',
      height: '0.9em',
      alignItems: 'center',
      mr: '0.6em',
    },
    endIcon: {
      fontSize: '1em',
      height: '0.9em',
      alignItems: 'center',
      ml: '0.6em',
    },
  },
  // Style variants - explicit overrides needed because utility functions reset these values
  variants: [
    {
      props: { variant: 'text' },
      style: {
        ...AppStyle.baseClickable(theme),
        letterSpacing: 0,
        textDecoration: 'underline',
        ...buttonShapeStyle,
      },
    },
    {
      props: { variant: 'contained' },
      style: {
        ...AppStyle.baseClickable(theme),
        ...containedButtonStyle,
        ...buttonShapeStyle,
        '&:hover': {
          ...containedButtonStyle,
          filter: 'brightness(0.92)',
          transform: BUTTON_HOVER_LIFT,
          boxShadow: CONTAINED_HOVER_SHADOW,
        },
      } as Interpolation<{theme: Theme}>,
    },
    {
      props: { variant: 'outlined' },
      style: {
        ...AppStyle.outlinedClickable(theme),
        ...buttonShapeStyle,
      } as Interpolation<{theme: Theme}>,
    },
    {
      props: { variant: 'light' },
      style: {
        ...AppStyle.baseClickable(theme),
        ...lightButtonStyle,
        ...buttonShapeStyle,
        border: `1px solid ${ColorTokens.neutral10} !important`,
        '&:hover': {
          ...lightButtonStyle,
          border: `1px solid ${ColorTokens.neutral10} !important`,
          backgroundColor: `${Palette.WASH_ME} !important`,
          transform: BUTTON_HOVER_LIFT,
          boxShadow: LIGHT_HOVER_SHADOW,
        },
      } as Interpolation<{theme: Theme}>,
    },
    {
      props: { variant: 'danger' },
      style: {
        ...AppStyle.baseClickable(theme),
        ...dangerButtonStyle,
        ...buttonShapeStyle,
        '&:hover': {
          ...dangerButtonStyle,
          filter: 'brightness(0.9)',
          transform: BUTTON_HOVER_LIFT,
          boxShadow: DANGER_HOVER_SHADOW,
        },
      } as Interpolation<{theme: Theme}>,
    },
    {
      props: { variant: 'dark' },
      style: {
        ...AppStyle.baseClickable(theme),
        ...darkButtonStyle,
        ...buttonShapeStyle,
        '&:hover': {
          ...darkButtonStyle,
          filter: 'brightness(0.92)',
          transform: BUTTON_HOVER_LIFT,
          boxShadow: DARK_HOVER_SHADOW,
        },
      } as Interpolation<{theme: Theme}>,
    },
    {
      props: { size: 'small' },
      style: {
        padding: '0.6em 1em',
      },
    },
    {
      props: { disabled: true },
      style: {
        ...DISABLED_CURSOR_STYLE,
        color: `${theme.palette.grey['100']} !important`,
        backgroundColor: `${theme.palette.background.paper} !important`,
        borderColor: `${theme.palette.grey['50']} !important`,
      },
    },
  ],
})
